import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button, Spinner, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";
import { showToastMessage, parseLocalDate, formatDateSplit } from "../../../../common/service/CommonService";
import CommonForm from "Components/Common/CommonForm";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { IP_PATHS } from "Routes/paths";
import {
  IP_UNIT_OPTIONS,
  Light_Condition_OPTIONS,
  MANUFACTURER_LABEL_OPTIONS,
  MODE_OF_RECEIPT_OPTIONS,
  PACKING_CONDITION_OPTIONS,
  DOSING_FORM_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { CheckListQuestion } from "modules/Library/Checklist/helpers/checklist.types";
import { IpInventoryFormValues, ContainerItem, CoaDetailRow } from "modules/Pharmacy/IPinventory/helpers/ipInventory.types";
import {
  getStudyProductTopFields,
  getStudyProductCategoryFields,
  getProductDetailsFields,
  getBatchDeliveryFields,
  getStorageConditionFields,
  getDataLoggerFields,
  getPharmacyQuarantineFields,
  getCoaTopFields,
  getIPCategoryLabel,
  SELECT_QUERY_FIELDS,
} from "../helpers/ipInventory.constant";
import {
  calculateDateRestrictions,
  checkIsFieldDisabled,
  buildInsertPayload,
  useIpStepPersistence,
} from "../helpers/ipInventory.helper";
import CoaDetailsTable from "../components/CoaDetailsTable";
import ContainerManagementSection from "../components/ContainerManagementSection";
import AttachmentManagementSection from "../components/AttachmentManagementSection";
import IpInventoryStepper from "../components/IpInventoryStepper";
import { queryClient } from "api/queryClient";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import FullPageLoader from "Components/Common/FullPageLoader";
import IpInventoryAcceptance from "./IpInventoryAcceptance";
import { validations } from "validations/FormValidations";
import SectionCard from "Components/Common/SectionCard";
import { useGlobalStudy } from "context/StudyContext";
import { BreadcrumbContext } from "Layouts/Header";

const AddIpInventory: React.FC = () => {
  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING, CONTEXT & STEPPERS                                           */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const { currentStep, handleStepChange } = useIpStepPersistence();

  /* ------------------------------------------------------------------------ */
  /* 2. LOCAL STATE & REFS                                                    */
  /* ------------------------------------------------------------------------ */
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [containersList, setContainersList] = useState<ContainerItem[]>([
    { containernumber: "01", initialquantity: "", containertype: null, containercondition: null, containerremarks: null, isusable: true },
  ]);
  const [containerError, setContainerError] = useState<string | null>(null);
  const [coaList, setCoaList] = useState<CoaDetailRow[]>([]);
  const [codelistsLoaded, setCodelistsLoaded] = useState(false);

  const [isStep1Saved, setIsStep1Saved] = useState<boolean>(false);
  const [createdIpId, setCreatedIpId] = useState<string | number | null>(null);
  const [createdReceiptNumber, setCreatedReceiptNumber] = useState<string | null>(null);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [coaError, setCoaError] = useState<string | null>(null);

  const validateContainersRef = useRef<(() => boolean) | null>(null);

  /* ------------------------------------------------------------------------ */
  /* 3. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    register,
    formState: { errors },
  } = useForm<IpInventoryFormValues>({
    defaultValues: {
      ipid: "",
      receiptnumber: "",
      studyid: "",
      studycode: "",
      sponsorname: "",
      sponsorid: "",
      ipcategory: "",
      productname: "",
      genericname: "",
      strength: "",
      dosageform: "",
      labelclaim: "",
      regulatoryofstudy: "",
      batchorlot: "batch",
      batchlotno: "",
      manufacturerlabel: "",
      manufacturingdate: "",
      typeofdate: "expiry",
      manufacturername: "",
      expirydate: "",
      lightcondition: "",
      receivedqty: "",
      actualreceivedqty: "",
      unit: "",
      totalcontainers: "",
      dateofreceipt: "",
      modeofreceipt: "",
      packingcondition: "",
      packingconditionremarks: null,
      qtyforstudy: "Sufficient",
      qtyforstudyremarks: null,
      qtyforretentionremarks: null,
      storagecondition: "",
      isdatalogger: "false",
      dataloggerid: null,
      dataloggerremarks: null,
      loggerprintobtained: null,
      tempexcursionacceptable: null,
      allottedpharmacyid: "",
      quarantineinstrumentid: "",
      quarantinestartdate: "",
      quarantineenddate: "",
      comments: "",
    },
    mode: "onBlur",
  });

  /* ------------------------------------------------------------------------ */
  /* 4. FORM WATCHERS                                                         */
  /* ------------------------------------------------------------------------ */
  const selectedStudyCode = watch("studycode");
  const selectedIP = watch("ipcategory");
  const selectedmanufacturingdate = watch("manufacturingdate");
  const selectedexpirydate = watch("expirydate");
  const selecteddateofreceipt = watch("dateofreceipt");
  const selectedquarantinestartdate = watch("quarantinestartdate");
  const selectedquarantineenddate = watch("quarantineenddate");
  const selectedcoareceiptdate = watch("coareceiptdate");
  const isDataLogger = watch("isdatalogger");
  const selectedLoggerPrint = watch("loggerprintobtained");
  const selectedTempExcursion = watch("tempexcursionacceptable");
  const selectedTypeOfDate = watch("typeofdate");
  const batchorlot = watch("batchorlot");
  const qtyForStudy = watch("qtyforstudy");
  const qtyForRetention = watch("qtyforretention");
  const selectedPackingCondition = watch("packingcondition");

  /* ------------------------------------------------------------------------ */
  /* 5. API & SERVICES QUERIES                                                */
  /* ------------------------------------------------------------------------ */
  const { InsertMutation, SelectIdQuery } = useIpInventoryService({
    studyid: globalStudy?.studyid,
    ipcategory: selectedIP,
  });
  const selectQuery = SelectIdQuery;

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  const { SelectAllQuery: checklistMasterQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "COA Detail",
  });

  /* ------------------------------------------------------------------------ */
  /* 6. (useMemo)                                   */
  /* ------------------------------------------------------------------------ */
  const selectQueryData = useMemo(() => {
    const rawData = selectQuery.data?.data;
    const ipData = Array.isArray(rawData) ? rawData[0] : rawData;
    const hasData = Boolean(ipData && (Array.isArray(rawData) ? rawData.length > 0 : Object.keys(ipData).length > 0));
    return { rawData, ipData, hasData };
  }, [selectQuery.data]);

  const { ipData: selectIpData, hasData: hasSelectQueryData } = selectQueryData;

  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        studyid: study.studyid,
        sponsorname: study.sponsorname,
        sponsorid: study.sponsorid,
        ipcategory: study.ipcategory,
      })) || [],
    [studySelectAllQuery.data]
  );

  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => s.value === selectedStudyCode),
    [selectedStudyCode, studyOptions]
  );

  const ipCategoryOptions = useMemo(() => {
    const codes = selectedStudy?.ipcategory || [];
    return codes.map((code: string) => ({
      label: getIPCategoryLabel(code),
      value: code,
    }));
  }, [selectedStudy]);

  const totalContainerQty = useMemo(() => {
    return containersList
      .filter((c) => !c.isExisting)
      .reduce((acc, curr) => acc + (Number(curr.initialquantity) || 0), 0);
  }, [containersList]);

  const dateRestrictions = useMemo(
    () =>
      calculateDateRestrictions(
        selectedmanufacturingdate,
        selecteddateofreceipt,
        selectedexpirydate,
        selectedquarantinestartdate
      ),
    [selectedmanufacturingdate, selecteddateofreceipt, selectedexpirydate, selectedquarantinestartdate]
  );

  const isSelectQueryFieldDisabled = useMemo(() => {
    if (!selectedIP) return true;
    return hasSelectQueryData;
  }, [selectedIP, hasSelectQueryData]);


  // COA file validation: requires a COA file in uploadedFiles (or existingAtt if applicable)
  const hasCoaFile = useMemo(() => {
    return (
      uploadedFiles.some(
        (f: any) => String(f.attachmenttype).toLowerCase() === "coa"
      )
    );
  }, [uploadedFiles]);

  const isFieldDisabled = (fieldName: any) => {
    if (SELECT_QUERY_FIELDS.includes(fieldName)) {
      if (isSelectQueryFieldDisabled) return true;
    }
    return checkIsFieldDisabled(
      fieldName,
      selectedStudyCode,
      selectedmanufacturingdate,
      selectedexpirydate,
      selecteddateofreceipt,
      selectedquarantinestartdate
    );
  };

  /* ------------------------------------------------------------------------ */
  /* 7. (useEffect)                                              */
  /* ------------------------------------------------------------------------ */

  // Document Title & Breadcrumbs
  useEffect(() => {
    document.title = "Add IP Inventory | ZENTIXS ORBIT";
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Investigational Product", link: IP_PATHS.LIST },
      { text: currentStep === 1 ? "Add IP Inventory" : "IP Acceptance Sheet" },
    ]);
    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [currentStep]);

  // Preload Dropdown Codelists
  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "IP_STORAGE_CONDITION_OPTIONS",
        "IP_UNIT_OPTIONS",
        "IP_PHYSICAL_CONDITION_OPTIONS",
        "IP_TEMP_LOGGER_STATUS_OPTIONS",
        "IP_TOTAL_CONTAINERS_OPTIONS",
        "Light_Condition_OPTIONS",
        "MANUFACTURER_LABEL_OPTIONS",
        "MODE_OF_RECEIPT_OPTIONS",
        "PACKING_CONDITION_OPTIONS",
        "DOSING_FORM_OPTIONS",
        "Checklist_Type_OPTIONS",
        "CONTAINER_CONDITION_OPTIONS",
      ]);

      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  // Handle Unverified Category Notice
  useEffect(() => {
    if (selectQuery.isFetching) return;
    const res = selectQuery.data;
    const verifiedCategory = res?.data?.[0]?.verifiedcategory;
    if (verifiedCategory === 1) {
      const msg = res?.message || res?.data?.message || "Please verify the existing IP category record before adding a new one.";
      showToastMessage("error", msg);
    }
  }, [selectQuery.data, selectQuery.isFetching]);

  // Sync Global Context Study with Form & Sponsor details
  useEffect(() => {
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matchedStudy = studyOptions.find(
        (s: any) => String(s.studyid) === String(globalStudy.studyid)
      );
      if (matchedStudy) {
        setValue("studycode", matchedStudy.value, { shouldValidate: true, shouldDirty: true });
        setValue("sponsorname", matchedStudy.sponsorname ?? "");
        setValue("sponsorid", matchedStudy.sponsorid ?? "");
      }
    } else {
      setValue("studycode", "", { shouldValidate: true, shouldDirty: true });
      setValue("sponsorname", "");
      setValue("sponsorid", "");
    }
  }, [globalStudy?.studyid, studyOptions, setValue]);

  // Populate or Reset Form Data when IP Category Query Changes
  useEffect(() => {
    if (hasSelectQueryData && selectIpData) {
      if (selectIpData.regulatoryofstudy) setValue("regulatoryofstudy", selectIpData.regulatoryofstudy, { shouldDirty: true });
      if (selectIpData.productname) setValue("productname", selectIpData.productname, { shouldDirty: true });
      if (selectIpData.genericname) setValue("genericname", selectIpData.genericname, { shouldDirty: true });
      if (selectIpData.strength) setValue("strength", selectIpData.strength, { shouldDirty: true });
      if (selectIpData.dosageform) setValue("dosageform", selectIpData.dosageform, { shouldDirty: true });
      if (selectIpData.labelclaim) setValue("labelclaim", selectIpData.labelclaim, { shouldDirty: true });
      if (selectIpData.lightcondition) setValue("lightcondition", selectIpData.lightcondition, { shouldDirty: true });
      if (selectIpData.batchorlot) setValue("batchorlot", selectIpData.batchorlot, { shouldDirty: true });
      if (selectIpData.batchlotno) setValue("batchlotno", selectIpData.batchlotno || "", { shouldDirty: true });
      if (selectIpData.manufacturerlabel) setValue("manufacturerlabel", selectIpData.manufacturerlabel, { shouldDirty: true });
      if (selectIpData.manufacturername) setValue("manufacturername", selectIpData.manufacturername, { shouldDirty: true });
      if (selectIpData.manufacturingdate) setValue("manufacturingdate", formatDateSplit(selectIpData.manufacturingdate), { shouldDirty: true });
      if (selectIpData.expirydate) setValue("expirydate", formatDateSplit(selectIpData.expirydate), { shouldDirty: true });
      if (selectIpData.storagecondition) setValue("storagecondition", selectIpData.storagecondition, { shouldDirty: true });
      if (selectIpData.typeofdate) setValue("typeofdate", selectIpData.typeofdate, { shouldDirty: true });
      if (selectIpData.unittype) setValue("unit", selectIpData.unittype, { shouldDirty: true });
      if (selectIpData.coareceiptdate) setValue("coareceiptdate", formatDateSplit(selectIpData.coareceiptdate), { shouldDirty: true });

      if (Array.isArray(selectIpData.containers) && selectIpData.containers.length > 0) {
        const existingMapped = selectIpData.containers.map((c: any, idx: number) => ({
          ipdetailid: c.ipdetailid,
          containernumber: c.containernumber || String(idx + 1).padStart(2, "0"),
          initialquantity: c.initialquantity ?? "",
          containertype: c.containertype || null,
          containercondition: c.containercondition || null,
          containerremarks: c.containerremarks || null,
          isusable: c.isusable !== false,
          isExisting: true,
        }));

        setContainersList([
          ...existingMapped,
          {
            containernumber: String(existingMapped.length + 1).padStart(2, "0"),
            initialquantity: "",
            containertype: null,
            containercondition: null,
            containerremarks: null,
            isusable: true,
            isExisting: false,
          },
        ]);
      }

      if (Array.isArray(selectIpData.coadetails) && selectIpData.coadetails.length > 0) {
        const mappedCoaList: CoaDetailRow[] = selectIpData.coadetails.map((detail: any) => ({
          coaid: detail.coaid ?? null,
          particulars: detail.checklistquestion,
          coaInfo: detail.coaanswer || "yes",
          labelInfo: detail.labelanswer || "yes",
        }));
        setCoaList(mappedCoaList);
      }
    } else if (selectQuery.isFetched) {
      reset({
        ipid: "",
        receiptnumber: "",
        studyid: globalStudy?.studyid || "",
        studycode: selectedStudyCode || "",
        sponsorname: getValues ? getValues("sponsorname") : "",
        sponsorid: getValues ? getValues("sponsorid") : "",
        ipcategory: selectedIP || "",
        productname: "",
        genericname: "",
        strength: "",
        dosageform: "",
        labelclaim: "",
        regulatoryofstudy: "",
        batchorlot: "batch",
        batchlotno: "",
        manufacturerlabel: "",
        manufacturingdate: "",
        typeofdate: "expiry",
        manufacturername: "",
        expirydate: "",
        lightcondition: "",
        receivedqty: "",
        actualreceivedqty: "",
        unit: "",
        totalcontainers: "",
        dateofreceipt: "",
        modeofreceipt: "",
        packingcondition: "",
        packingconditionremarks: null,
        qtyforstudy: "Sufficient",
        qtyforstudyremarks: null,
        qtyforretentionremarks: null,
        storagecondition: "",
        isdatalogger: "false",
        dataloggerid: null,
        dataloggerremarks: null,
        loggerprintobtained: null,
        tempexcursionacceptable: null,
        allottedpharmacyid: "",
        quarantineinstrumentid: "",
        quarantinestartdate: "",
        quarantineenddate: "",
        comments: "",
      });

      setContainersList([
        {
          containernumber: "01",
          initialquantity: "",
          containertype: null,
          containercondition: null,
          containerremarks: null,
          isusable: true,
          isExisting: false,
        },
      ]);

      setUploadedFiles([]);
    }
  }, [hasSelectQueryData, selectIpData, selectQuery.isFetched, setValue, reset, getValues, selectedStudyCode, selectedIP, globalStudy?.studyid, checklistMasterQuery?.data?.data]);

  // Reset packing condition remarks if not unsealed
  useEffect(() => {
    if (!selectedPackingCondition) return;
    if (PACKING_CONDITION_OPTIONS.length > 0) {
      const matchedOpt = PACKING_CONDITION_OPTIONS.find(
        (opt: any) => String(opt.value) === String(selectedPackingCondition)
      );
      const label = matchedOpt ? matchedOpt.label : String(selectedPackingCondition || "");
      if (String(label).trim().toLowerCase() !== "unsealed") {
        setValue("packingconditionremarks", null);
      }
    }
  }, [selectedPackingCondition, setValue]);

  // Reset study quantity remarks if sufficient
  useEffect(() => {
    if (qtyForStudy !== "Insufficient") {
      setValue("qtyforstudyremarks", null);
    }
  }, [qtyForStudy, setValue]);

  // Reset retention quantity remarks if sufficient
  useEffect(() => {
    if (qtyForRetention !== "Insufficient") {
      setValue("qtyforretentionremarks", null);
    }
  }, [qtyForRetention, setValue]);

  // Data logger fields reset dependent on toggle
  useEffect(() => {
    if (isDataLogger === "false") {
      setValue("dataloggerid", null);
      setValue("loggerprintobtained", null);
      setValue("tempexcursionacceptable", null);
    } else if (isDataLogger === "true") {
      const isPrintNoOrNa = selectedLoggerPrint === "no" || selectedLoggerPrint === "na";
      const isTempNoOrNa = selectedTempExcursion === "no" || selectedTempExcursion === "na";
      if (!isPrintNoOrNa && !isTempNoOrNa) {
        setValue("dataloggerremarks", null);
      }
    }
  }, [isDataLogger, selectedLoggerPrint, selectedTempExcursion, setValue]);

  // Pre-fill COA checklist questions
  useEffect(() => {
    if (selectIpData?.coadetails && Array.isArray(selectIpData.coadetails) && selectIpData.coadetails.length > 0) {
      return;
    }

    const rawQuestions: CheckListQuestion[] = checklistMasterQuery?.data?.data ?? [];
    if (!rawQuestions.length || !codelistsLoaded) return;

    const dynamicCoaList: CoaDetailRow[] = rawQuestions.map((q) => ({
      particulars: q.question,
      coaInfo: "yes",
      labelInfo: "yes",
    }));
    setCoaList(dynamicCoaList);
  }, [checklistMasterQuery?.data?.data, codelistsLoaded, setCoaList, selectIpData]);

  // Date restriction validation watchers
  useEffect(() => {
    const mfgDate = parseLocalDate(selectedmanufacturingdate);
    const expDate = parseLocalDate(selectedexpirydate);
    const receiptDate = parseLocalDate(selecteddateofreceipt);
    const coaDate = parseLocalDate(selectedcoareceiptdate);
    const qStartDate = parseLocalDate(selectedquarantinestartdate);
    const qEndDate = parseLocalDate(selectedquarantineenddate);

    if (!mfgDate) return;

    if (expDate && expDate < mfgDate) {
      setValue("expirydate", "", { shouldValidate: true });
      setValue("dateofreceipt", "", { shouldValidate: true });
      if (!hasSelectQueryData) setValue("coareceiptdate", "", { shouldValidate: true });
      setValue("quarantinestartdate", "", { shouldValidate: true });
      setValue("quarantineenddate", "", { shouldValidate: true });
      return;
    }

    if (receiptDate) {
      if (receiptDate < mfgDate || (expDate && receiptDate > expDate)) {
        setValue("dateofreceipt", "", { shouldValidate: true });
        if (!hasSelectQueryData) setValue("coareceiptdate", "", { shouldValidate: true });
        setValue("quarantinestartdate", "", { shouldValidate: true });
        setValue("quarantineenddate", "", { shouldValidate: true });
        return;
      }
    }

    if (!hasSelectQueryData && coaDate && receiptDate) {
      if (coaDate < receiptDate) {
        setValue("coareceiptdate", "", { shouldValidate: true });
      }
    }

    if (qStartDate && receiptDate) {
      if (qStartDate < receiptDate) {
        setValue("quarantinestartdate", "", { shouldValidate: true });
        setValue("quarantineenddate", "", { shouldValidate: true });
        return;
      }
    }

    if (qEndDate && qStartDate) {
      if (qEndDate < qStartDate) {
        setValue("quarantineenddate", "", { shouldValidate: true });
      }
    }
  }, [
    selectedmanufacturingdate,
    selectedexpirydate,
    selecteddateofreceipt,
    selectedcoareceiptdate,
    selectedquarantinestartdate,
    selectedquarantineenddate,
    setValue,
    hasSelectQueryData,
  ]);


  // Clear COA error when COA file is added
  useEffect(() => {
    if (hasCoaFile) setCoaError(null);
  }, [hasCoaFile]);

  /* ------------------------------------------------------------------------ */
  /* 8. EVENT HANDLERS & FORM SUBMISSION                                      */
  /* ------------------------------------------------------------------------ */
  const handlePreview = (file: any) => {
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
    }
  };

  const handleCoaChange = (index: number, col: "coaInfo" | "labelInfo", value: "yes" | "no" | "na") => {
    setCoaList((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [col]: value } : item))
    );
  };

  const onSubmit = (data: IpInventoryFormValues) => {
    if (validateContainersRef.current && !validateContainersRef.current()) return;

    if (!hasCoaFile) {
      setCoaError("At least one Certificate of Analysis (COA) document is required.");
      return;
    }

    setCoaError(null);
    handleInsert(data);
  };

  const onInvalid = () => {
    if (validateContainersRef.current) validateContainersRef.current();
  };

  const handleInsert = (data: IpInventoryFormValues) => {
    if (!selectedStudy) {
      showToastMessage("error", "Please select study");
      return;
    }

    const payload = buildInsertPayload(
      data,
      selectedStudy,
      containersList,
      totalContainerQty,
      coaList,
      uploadedFiles
    );

    InsertMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["ipinventory-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message || "IP Inventory added successfully");
        const resData = response?.data?.[0] || response?.data;
        const newIpId = resData?.ipid;
        const newReceiptNo = resData?.receiptnumber;
        if (newIpId) {
          setCreatedIpId(newIpId);
        }
        if (newReceiptNo) {
          setCreatedReceiptNumber(newReceiptNo);
        }
        setIsStep1Saved(true);
      },
      onError: (err: any) => {
        showToastMessage("error", err);
      },
    });
  };

  /* ------------------------------------------------------------------------ */
  /* 9. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  if (selectQuery.isLoading || selectQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        {/* Stepper Header Navigation */}
        <IpInventoryStepper
          currentStep={currentStep}
          onStepChange={handleStepChange}
          isStep1Saved={isStep1Saved}
        />

        {currentStep === 1 ? (
          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            {/* Section 1: Study & Product Details */}
            <SectionCard
              index={1}
              icon="ri-microscope-line"
              title="Study & Product Details"
              subtitle="Select the study protocol and enter product information."
            >
              <CommonForm fields={getStudyProductTopFields(studyOptions, isFieldDisabled)} control={control} errors={errors} columns={2} />
              <div className="mt-3">
                <CommonForm fields={getStudyProductCategoryFields(selectedStudyCode, null, ipCategoryOptions)} control={control} errors={errors} columns={1} />
              </div>
              <div className="mt-3">
                <CommonForm fields={getProductDetailsFields(isFieldDisabled, DOSING_FORM_OPTIONS, Light_Condition_OPTIONS, MANUFACTURER_LABEL_OPTIONS, dateRestrictions, selectedTypeOfDate, batchorlot)} control={control} errors={errors} columns={3} />
              </div>
            </SectionCard>

            {/* Section 2: Batch & Delivery Information */}
            <SectionCard
              index={2}
              icon="ri-archive-line"
              title="Batch & Delivery Information"
              subtitle="Provide batch numbers, quantities, and delivery details."
            >
              <CommonForm fields={getBatchDeliveryFields(selectedStudyCode, IP_UNIT_OPTIONS, MODE_OF_RECEIPT_OPTIONS, PACKING_CONDITION_OPTIONS, dateRestrictions, isFieldDisabled, qtyForStudy, qtyForRetention, selectedPackingCondition)} control={control} errors={errors} columns={3} />

              <ContainerManagementSection
                containersList={containersList}
                setContainersList={setContainersList}
                containerError={containerError}
                setContainerError={setContainerError}
                selectedStudyCode={selectedStudyCode}
                setValue={setValue}
                onRegisterValidate={(fn) => {
                  validateContainersRef.current = fn;
                }}
                isDisabled={!selectedIP || !selectedStudyCode}
              />
            </SectionCard>

            {/* Section 3: Storage & Condition Assessment */}
            <SectionCard
              index={3}
              icon="ri-temp-cold-line"
              title="Storage & Condition Assessment"
              subtitle="Assess storage requirements and physical condition on receipt."
            >
              <CommonForm fields={getStorageConditionFields(selectedStudyCode, isFieldDisabled)} control={control} errors={errors} columns={2} />
            </SectionCard>

            {/* Section 4: Data Logger Details */}
            <SectionCard
              index={4}
              icon="ri-cpu-line"
              title="Data Logger Details"
              subtitle="Record data logger accompaniment, ID, printout, temperature excursion, and recorder details."
            >
              <CommonForm fields={getDataLoggerFields(selectedStudyCode, isDataLogger === "true", selectedLoggerPrint, selectedTempExcursion)} control={control} errors={errors} columns={4} />
            </SectionCard>

            {/* Section 5: Pharmacy / Quarantine Details */}
            <SectionCard
              index={5}
              icon="ri-shield-check-line"
              title="Pharmacy / Quarantine Details"
              subtitle="Record allotted pharmacy ID, quarantine details, instrument IDs, and storage dates."
            >
              <CommonForm fields={getPharmacyQuarantineFields(selectedStudyCode, dateRestrictions, isFieldDisabled)} control={control} errors={errors} columns={3} />
            </SectionCard>

            {/* Section 6: COA Details */}
            <SectionCard
              index={6}
              icon="ri-file-list-3-line"
              title="COA Details"
              subtitle="Verify COA information and label information compliance across all parameters."
            >
              <div className="mb-3">
                <CommonForm
                  fields={getCoaTopFields(selectedStudyCode, dateRestrictions, isFieldDisabled)}
                  control={control}
                  errors={errors}
                  columns={3}
                />
              </div>
              <CoaDetailsTable
                coaList={coaList}
                onCoaChange={handleCoaChange}
                isDisabled={isSelectQueryFieldDisabled || !selectedStudyCode}
              />
            </SectionCard>

            {/* Section 7: Attachments & Verification */}
            <SectionCard
              index={7}
              icon="ri-attachment-2"
              title="Attachments & Verification"
              subtitle="Upload supporting documents and confirm receipt details."
            >
              <AttachmentManagementSection
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                existingAttachments={[]}
                setExistingAttachments={() => { }}
                deletedAttachmentIds={[]}
                setDeletedAttachmentIds={() => { }}
                selectedStudyCode={selectedStudyCode}
                handlePreview={handlePreview}
                coaError={coaError}
              />

              <Row>
                <Col>
                  <label className="form-label text-dark small fw-bold">
                    Additional Comments / Discrepancies
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter any notes, observations, or discrepancy details..."
                    {...register("comments", validations.ipInventory.comments)}
                    style={{ fontSize: 13, resize: "none", textAlign: "justify" }}
                    disabled={!selectedStudyCode}
                  />
                  {errors.comments && (
                    <div className="text-danger small mt-1">
                      {errors.comments.message}
                    </div>
                  )}
                </Col>
              </Row>
            </SectionCard>

            {/* Sticky Action Footer */}
            <div className={formStyles.stickyFooter}>
              <div className="d-flex justify-content-end gap-2 w-100">
                <Button
                  type="submit"
                  color="primary"
                  outline
                  disabled={isStep1Saved || InsertMutation.isPending}
                >
                  {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
                  Save
                </Button>
                <Button
                  type="button"
                  color="primary"
                  outline
                  disabled={!isStep1Saved}
                  onClick={() => handleStepChange(2)}
                >
                  Next
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <IpInventoryAcceptance
            ipid={createdIpId}
            receiptnumber={createdReceiptNumber}
            onBack={() => handleStepChange(1)}
            isStepperMode={true}
          />
        )}

        {/* Document Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </Container>
    </div>
  );
};

export default AddIpInventory;